import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { debounce } from 'lodash';
import { toast } from 'react-toastify';
import { Modal, ModalHeader, ModalBody, ModalFooter, Form, Input, Label, FormGroup, Button } from 'reactstrap';
import ConfirmCloseModal from '../Common/ConfirmCloseModal';
import ErrorAlert from '../Common/ErrorAlert';
import Select from "react-select";
import { useDispatch, useSelector } from 'react-redux';
import {
    getLegacyMarcas,
    getLegacyModelos,
    getLegacyColores,
    getLegacyTiposCombustible,
    getLegacyTiposTraccion,
    getLegacyTiposTransmision,
    getLegacyPersonas
} from '../../slices/thunks';
import {
    parseMarca,
    parseModelo,
    parseColor,
    parseTiposTraccion,
    parseTiposTransmision,
    parseTiposCombustible,
    buscarNombreMarca,
    buscarNombreModelo,
    buscarColor,
    buscarTipoCombustible,
    buscarTipoTraccion,
    buscarTipoTransmision
} from '../utils/vehicleLookups';
import { getClienteById } from '../../helpers/api_helper';
import { formatRUT } from '../../utils/rut';
import useModalForm from '../Hooks/useModalForm';
import useUpdateVehiculo from '../Hooks/useUpdateVehiculo';
import useObtenerVehiculoPorPatente from '../Hooks/useObtenerVehiculoPorPatente';
import LoadingOverlay from '../Common/LoadingOverlay';
import ModalCrearRut from './ModalCrearRut';
import ModalModificarCliente from './ModalModificarCliente';

export const normalizeRUT = (rut) => rut.replace(/\./g, '').trim();

const EMPTY_FORM = {
    patente: '',
    marca: {},
    modelo: {},
    vin: '',
    chasis: '',
    color: {},
    tipoCombustible: {},
    tipoTraccion: {},
    tipoTransmision: {},
    ano: '',
    motor: '',
    rut: '',
};

const ModalFormVehiculo = ({ mode = 'create', isOpen, toggle, onSave, selectedVehiculo, patente }) => {
    const tenantId = useSelector((state) => state.Context.tenantId);
    const empresa = useSelector((state) => state.Context.empresa);

    const isCreate = mode === 'create';

    const [ownerLookupResult, setOwnerLookupResult] = useState(null);
    const [isOwnerModalOpen, setIsOwnerModalOpen] = useState(false);
    const [ownerModalMode, setOwnerModalMode] = useState('create');
    const [lookupRut, setLookupRut] = useState('');
    const [selectErrors, setSelectErrors] = useState({});
    const abortControllerRef = useRef(null);

    const validateSelectFields = useCallback((data) => {
        const errors = {};
        if (!data.marca?.value) errors.marca = 'Marca es obligatoria';
        if (!data.modelo?.value) errors.modelo = 'Modelo es obligatorio';
        if (!data.color?.value) errors.color = 'Color es obligatorio';
        if (!data.tipoCombustible?.value) errors.tipoCombustible = 'Combustible es obligatorio';
        if (!data.tipoTraccion?.value) errors.tipoTraccion = 'Tracción es obligatoria';
        if (!data.tipoTransmision?.value) errors.tipoTransmision = 'Transmisión es obligatoria';
        return errors;
    }, []);

    const clearSelectError = useCallback((fieldName) => {
        setSelectErrors(prev => {
            if (!prev[fieldName]) return prev;
            const next = { ...prev };
            delete next[fieldName];
            return next;
        });
    }, []);

    const createOnSave = useCallback(async (formData) => {
        const errors = validateSelectFields(formData);
        if (Object.keys(errors).length > 0) {
            setSelectErrors(errors);
            throw new Error('Complete los campos obligatorios');
        }
        if (!formData.rut || !formData.rut.trim()) {
            throw new Error('El RUT del propietario es obligatorio y debe corresponder a un cliente existente.');
        }
        if (ownerLookupResult !== 'found') {
            throw new Error('El RUT del propietario es obligatorio y debe corresponder a un cliente existente.');
        }
        await onSave(formData);
    }, [onSave, ownerLookupResult, validateSelectFields]);

    const {
        formData: createFormData, setFormData: setCreateFormData, resetForm,
        loading: createLoading, error: createError,
        isDirty: createIsDirty,
        handleSubmit: createHandleSubmit, handleCancel: createHandleCancel,
        showConfirm: createShowConfirm, setShowConfirm: setCreateShowConfirm,
    } = useModalForm({
        initialData: { ...EMPTY_FORM, empresa },
        onSave: isCreate ? createOnSave : (() => {}),
        onClose: toggle,
    });

    const [modifyFormData, setModifyFormData] = useState({ ...EMPTY_FORM });
    const [modifyIsDirty, setModifyIsDirty] = useState(false);
    const [modifyShowConfirm, setModifyShowConfirm] = useState(false);
    const [saveError, setSaveError] = useState('');
    const [modeloPendiente, setModeloPendiente] = useState(null);


    const marcas = useSelector((state) => state.LegacyMarcas.legacyMarcas);
    const modelos = useSelector((state) => state.LegacyModelos.legacyModelos);
    const colores = useSelector((state) => state.LegacyColores.legacyColores);
    const tiposCombustible = useSelector((state) => state.LegacyTiposCombustible.legacyTiposCombustible);
    const tiposTraccion = useSelector((state) => state.LegacyTiposTraccion.legacyTiposTraccion);
    const tiposTransmision = useSelector((state) => state.LegacyTiposTransmision.legacyTiposTransmision);
    const personas = useSelector((state) => state.LegacyPersonas.legacyPersonas);
    const dispatch = useDispatch();

    const { vehiculo, loading: loadingVehiculo } = useObtenerVehiculoPorPatente(
        !isCreate && isOpen ? selectedVehiculo : null
    );
    const { updateVehiculo, loading: updating } = useUpdateVehiculo();

    const formData = isCreate ? createFormData : modifyFormData;
    const setFormDataForMode = isCreate ? setCreateFormData : setModifyFormData;
    const loading = isCreate ? createLoading : updating;
    const modalIsDirty = isCreate ? createIsDirty : modifyIsDirty;
    const showConfirm = isCreate ? createShowConfirm : modifyShowConfirm;
    const error = isCreate ? createError : saveError;

    const setShowConfirm = isCreate ? setCreateShowConfirm : setModifyShowConfirm;

    const setIsDirty = useCallback((val) => {
        if (!isCreate) setModifyIsDirty(val);
    }, [isCreate]);

    const setFormData = useCallback((valueOrUpdater) => {
        setFormDataForMode(valueOrUpdater);
    }, [setFormDataForMode]);

    useEffect(() => {
        dispatch(getLegacyMarcas({ tenantId, empresa }));
        dispatch(getLegacyColores({ tenantId, empresa }));
        dispatch(getLegacyTiposCombustible({ tenantId, empresa }));
        dispatch(getLegacyTiposTraccion({ tenantId, empresa }));
        dispatch(getLegacyTiposTransmision({ tenantId, empresa }));
        if (!isCreate) {
            dispatch(getLegacyPersonas({ tenantId, empresa }));
        }
    }, [tenantId, empresa, isCreate, dispatch]);

    useEffect(() => {
        if (!isCreate && vehiculo && vehiculo.CLIENT) {
            dispatch(getLegacyPersonas({ tenantId, rut: vehiculo.CLIENT, empresa }));
        }
    }, [vehiculo, tenantId, empresa, isCreate, dispatch]);

    useEffect(() => {
        if (isCreate && isOpen) {
            resetForm();
            setOwnerLookupResult(null);
            setSelectErrors({});
        }
        if (!isCreate && isOpen) {
            setModifyIsDirty(false);
            setSaveError('');
            setOwnerLookupResult(null);
            setSelectErrors({});
        }
    }, [isOpen, isCreate, resetForm]);

    useEffect(() => {
        if (isCreate && isOpen && patente) {
            setCreateFormData(prev => ({ ...prev, patente }));
        }
    }, [isCreate, isOpen, patente, setCreateFormData]);

    useEffect(() => {
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    const handleRutChange = useCallback(
        debounce(async (rut, tenantId) => {
            const normalizedRut = normalizeRUT(rut);
            if (!normalizedRut) {
                setOwnerLookupResult(null);
                return;
            }

            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }

            const controller = new AbortController();
            abortControllerRef.current = controller;

            setLookupRut(normalizedRut);
            try {
                const response = await getClienteById(normalizedRut, { tenantId, signal: controller.signal });
                if (controller.signal.aborted) return;

                if (response?.CLIPRO) {
                    setOwnerLookupResult('found');
                } else {
                    setOwnerLookupResult('not_found');
                }
            } catch (err) {
                if (controller.signal.aborted) return;
                setOwnerLookupResult('error');
            }
        }, 400),
        [tenantId]
    );

    useEffect(() => {
        if (!isCreate && vehiculo && Object.keys(vehiculo).length > 0) {
            setModifyFormData({
                patente: vehiculo.PATENT?.trim() || '',
                marca: { value: vehiculo.MARCAS, label: buscarNombreMarca(vehiculo.MARCAS, marcas) },
                vin: vehiculo.NROVIN || '',
                chasis: vehiculo.NCHASI || '',
                color: { label: buscarColor(vehiculo.CCOLOR, colores), value: vehiculo.CCOLOR },
                tipoCombustible: { label: buscarTipoCombustible(vehiculo.COMBUS, tiposCombustible), value: vehiculo.COMBUS },
                tipoTraccion: { label: buscarTipoTraccion(vehiculo.TRACCI, tiposTraccion), value: vehiculo.TRACCI },
                tipoTransmision: { label: buscarTipoTransmision(vehiculo.TRASMI, tiposTransmision), value: vehiculo.TRASMI },
                ano: vehiculo.ANOFAB || '',
                motor: vehiculo.NMOTOR || '',
                rut: vehiculo.CLIENT || '',
            });
            setModeloPendiente(vehiculo.MODELO);
            setModifyIsDirty(true);
            if (vehiculo.CLIENT) {
                handleRutChange(vehiculo.CLIENT, tenantId);
            }
        }
    }, [vehiculo, isCreate, handleRutChange, tenantId]);

    useEffect(() => {
        if (formData.marca?.value) {
            dispatch(getLegacyModelos({ tenantId, empresa, marca: formData.marca?.value }));
        }
    }, [formData.marca, tenantId, empresa, dispatch]);

    useEffect(() => {
        if (!isCreate && Array.isArray(modelos) && modelos.length > 0 && modeloPendiente) {
            const m = { value: modeloPendiente, label: buscarNombreModelo(modeloPendiente, modelos) };
            setModeloPendiente(null);
            setModifyFormData(prev => ({ ...prev, modelo: m }));
        }
    }, [modelos, isCreate, modeloPendiente]);

    const handleInputChange = useCallback((e) => {
        if (!isCreate) {
            setIsDirty(true);
            setSaveError('');
        }
        if (e?.target) {
            const { name, value } = e.target;
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    }, [isCreate, setIsDirty, setFormData]);

    const handleMarcaChange = useCallback((e) => {
        if (!isCreate) setIsDirty(true);
        clearSelectError('marca');
        if (e) {
            if (!e.label) {
                e.label = buscarNombreMarca(e.value, marcas);
            }
            setFormData(prev => ({ ...prev, marca: e, modelo: {} }));
        }
    }, [isCreate, setIsDirty, marcas, setFormData, clearSelectError]);

    const handleModeloChange = useCallback((e) => {
        if (!isCreate) setIsDirty(true);
        clearSelectError('modelo');
        setFormData(prev => ({ ...prev, modelo: e }));
    }, [isCreate, setIsDirty, setFormData, clearSelectError]);

    const handleColorChange = useCallback((e) => {
        if (!isCreate) setIsDirty(true);
        clearSelectError('color');
        if (e) {
            if (!e.label) {
                e.label = buscarColor(e.value, colores);
            }
            setFormData(prev => ({ ...prev, color: e }));
        }
    }, [isCreate, setIsDirty, colores, setFormData, clearSelectError]);

    const handleSelectCombustible = useCallback((opcion) => {
        if (!isCreate) setIsDirty(true);
        clearSelectError('tipoCombustible');
        setFormData(prev => ({ ...prev, tipoCombustible: opcion }));
    }, [isCreate, setIsDirty, setFormData, clearSelectError]);

    const handleSelectTraccion = useCallback((opcion) => {
        if (!isCreate) setIsDirty(true);
        clearSelectError('tipoTraccion');
        setFormData(prev => ({ ...prev, tipoTraccion: opcion }));
    }, [isCreate, setIsDirty, setFormData, clearSelectError]);

    const handleSelectTransmision = useCallback((opcion) => {
        if (!isCreate) setIsDirty(true);
        clearSelectError('tipoTransmision');
        setFormData(prev => ({ ...prev, tipoTransmision: opcion }));
    }, [isCreate, setIsDirty, setFormData, clearSelectError]);

    const handleOwnerSave = useCallback((ownerFormData) => {
        setFormData(prev => ({ ...prev, rut: formatRUT(ownerFormData.CLIPRO) }));
        setIsOwnerModalOpen(false);
        setOwnerLookupResult('found');
    }, [setFormData]);

    const handleOpenOwnerModal = useCallback((mode) => {
        setOwnerModalMode(mode);
        setIsOwnerModalOpen(true);
    }, []);

    const handleSubmitModify = useCallback(async (e) => {
        e.preventDefault();
        setSaveError('');

        const errors = validateSelectFields(modifyFormData);
        if (Object.keys(errors).length > 0) {
            setSelectErrors(errors);
            setSaveError('Complete los campos obligatorios');
            return;
        }

        if (!modifyFormData.rut || !modifyFormData.rut.trim()) {
            setSaveError('El RUT del propietario es obligatorio y debe corresponder a un cliente existente.');
            return;
        }
        if (ownerLookupResult !== 'found') {
            setSaveError('El RUT del propietario es obligatorio y debe corresponder a un cliente existente.');
            return;
        }

        const result = await updateVehiculo(selectedVehiculo, modifyFormData);
        if (result.success) {
            setModifyIsDirty(false);
            onSave();
            toggle();
        } else {
            setSaveError(result.error || 'Error al actualizar vehículo');
        }
    }, [updateVehiculo, selectedVehiculo, modifyFormData, ownerLookupResult, onSave, toggle, validateSelectFields]);

    const safeToggle = useCallback(() => {
        if (loading) return;
        if (modalIsDirty) {
            setShowConfirm(true);
            return;
        }
        toggle();
    }, [loading, modalIsDirty, setShowConfirm, toggle]);

    const handleCancel = useCallback(() => {
        if (loading) return;
        if (isCreate) {
            createHandleCancel();
        } else {
            if (modifyIsDirty) {
                setModifyShowConfirm(true);
                return;
            }
            toggle();
        }
    }, [loading, isCreate, createHandleCancel, modifyIsDirty, toggle]);

    const handleConfirmClose = useCallback(() => {
        if (loading) return;
        setShowConfirm(false);
        toggle();
    }, [loading, setShowConfirm, toggle]);


    const onSubmit = isCreate ? createHandleSubmit : handleSubmitModify;

    return (
        <Modal isOpen={isOpen} toggle={safeToggle} size="lg" backdrop="static">
            <ModalHeader toggle={safeToggle}>
                <i className="ri-car-line me-2"></i>
                {isCreate ? 'Crear Vehículo' : 'Vehículo'}
            </ModalHeader>
            <Form onSubmit={onSubmit}>
                <ModalBody style={{ position: 'relative' }}>
                    <LoadingOverlay loading={loading} message={isCreate ? 'Guardando vehículo…' : 'Actualizando vehículo…'} />

                    {!isCreate && loadingVehiculo && (
                        <div className="text-center">Cargando datos del vehículo...</div>
                    )}

                    {(!isCreate && !loadingVehiculo) || isCreate ? (
                        <>
                            {error && (
                                <ErrorAlert error={error} onDismiss={() => isCreate ? null : setSaveError('')} />
                            )}

                            <div className="row">
                                <div className="col-6">
                                    <FormGroup>
                                        <Label for="patente">Patente</Label>
                                        <Input
                                            type="text"
                                            id="patente"
                                            name="patente"
                                            value={formData.patente}
                                            onChange={handleInputChange}
                                            placeholder="Ej: AB1234"
                                            required
                                            readOnly={!isCreate || Boolean(patente)}
                                        />
                                    </FormGroup>
                                </div>
                                <div className="col-6">
                                    <FormGroup>
                                        <Label for="rut">Rut Propietario</Label>
                                        <div className="d-flex gap-2">
                                                <Input
                                                    type="text"
                                                    id="rut"
                                                    name="rut"
                                                    value={formData.rut}
                                                    onChange={(e) => {
                                                        const formatted = formatRUT(e.target.value);
                                                        handleInputChange({ target: { name: 'rut', value: formatted } });
                                                        handleRutChange(formatted, tenantId);
                                                    }}
                                                    placeholder="Ej: 78123456-7"
                                                />
                                            {ownerLookupResult === 'not_found' && (
                                                <Button
                                                    color="primary"
                                                    data-testid="owner-lookup-crear-button"
                                                    onClick={() => handleOpenOwnerModal('create')}
                                                >
                                                    Crear
                                                </Button>
                                            )}
                                            {ownerLookupResult === 'found' && (
                                                <Button
                                                    color="warning"
                                                    data-testid="owner-lookup-modificar-button"
                                                    onClick={() => handleOpenOwnerModal('modify')}
                                                >
                                                    Modificar
                                                </Button>
                                            )}
                                            {ownerLookupResult === 'error' && (
                                                <span
                                                    className="text-danger"
                                                    data-testid="owner-lookup-error"
                                                >
                                                    Error al buscar cliente
                                                </span>
                                            )}
                                        </div>
                                    </FormGroup>
                                </div>
                            </div>

                            <div className="d-flex align-items-center my-4">
                                <div className="flex-grow-1 border-top"></div>
                                <span className="mx-3 text-muted">Información del Vehículo</span>
                                <div className="flex-grow-1 border-top"></div>
                            </div>

                            <div className="row">
                                <div className="col-6">
                                    <FormGroup>
                                        <Label for="marca">Marca *</Label>
                                        <Select
                                            id="marca"
                                            placeholder="Marcas..."
                                            value={formData.marca}
                                            onChange={handleMarcaChange}
                                            options={Array.isArray(marcas) && marcas.length > 0 ? marcas?.map(parseMarca) : []}
                                            isClearable 
                                        />
                                        {selectErrors.marca && (
                                            <div className="text-danger small mt-1">{selectErrors.marca}</div>
                                        )}
                                    </FormGroup>
                                </div>
                                <div className="col-6">
                                    <FormGroup>
                                        <Label for="modelo">Modelo *</Label>
                                        <Select
                                            id="modelo"
                                            placeholder="Modelos..."
                                            value={formData.modelo}
                                            onChange={handleModeloChange}
                                            options={Array.isArray(modelos) && modelos.length > 0 ? modelos?.map(parseModelo) : []}
                                            isClearable 
                                        />
                                        {selectErrors.modelo && (
                                            <div className="text-danger small mt-1">{selectErrors.modelo}</div>
                                        )}
                                    </FormGroup>
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-6">
                                    <FormGroup>
                                        <Label for="vin">VIN</Label>
                                        <Input
                                            type="text"
                                            id="vin"
                                            name="vin"
                                            value={formData.vin}
                                            onChange={handleInputChange}
                                            placeholder="Ej: 1FM4K7B52FEB67263"
                                        />
                                    </FormGroup>
                                </div>
                                <div className="col-6">
                                    <FormGroup>
                                        <Label for="chasis">Chasis</Label>
                                        <Input
                                            type="text"
                                            id="chasis"
                                            name="chasis"
                                            value={formData.chasis}
                                            onChange={handleInputChange}
                                            placeholder="Ej: 4P3DTFA3WDE902777"
                                        />
                                    </FormGroup>
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-6">
                                    <FormGroup>
                                        <Label for="ano">Año</Label>
                                        <Input
                                            type="text"
                                            id="ano"
                                            name="ano"
                                            value={formData.ano}
                                            onChange={handleInputChange}
                                            placeholder="Ej: 2020"
                                        />
                                    </FormGroup>
                                </div>
                                <div className="col-6">
                                    <FormGroup>
                                        <Label for="motor">Motor</Label>
                                        <Input
                                            type="text"
                                            id="motor"
                                            name="motor"
                                            value={formData.motor}
                                            onChange={handleInputChange}
                                            placeholder="Ej: 67195000539462"
                                        />
                                    </FormGroup>
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-6">
                                    <FormGroup>
                                        <Label for="color">Color *</Label>
                                        <Select
                                            id="color"
                                            placeholder="Colores..."
                                            value={formData.color}
                                            onChange={handleColorChange}
                                            options={Array.isArray(colores) && colores.length > 0 ? colores?.map(parseColor) : []}
                                            isClearable
                                        />
                                        {selectErrors.color && (
                                            <div className="text-danger" style={{ fontSize: '0.875em', marginTop: '0.25rem' }}>{selectErrors.color}</div>
                                        )}
                                    </FormGroup>
                                </div>
                                <div className="col-6">
                                    <FormGroup>
                                        <Label for="transmision">Transmisión *</Label>
                                        <Select
                                            id="transmision"
                                            placeholder="Seleccione..."
                                            value={formData.tipoTransmision}
                                            onChange={handleSelectTransmision}
                                            options={Array.isArray(tiposTransmision) && tiposTransmision.length > 0 ? tiposTransmision?.map(parseTiposTransmision) : []}
                                            isClearable
                                        />
                                        {selectErrors.tipoTransmision && (
                                            <div className="text-danger" style={{ fontSize: '0.875em', marginTop: '0.25rem' }}>{selectErrors.tipoTransmision}</div>
                                        )}
                                    </FormGroup>
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-6">
                                    <FormGroup>
                                        <Label for="combustible">Combustible *</Label>
                                        <Select
                                            id="combustible"
                                            placeholder="Seleccione..."
                                            value={formData.tipoCombustible}
                                            onChange={handleSelectCombustible}
                                            options={Array.isArray(tiposCombustible) && tiposCombustible.length > 0 ? tiposCombustible?.map(parseTiposCombustible) : []}
                                            isClearable 
                                        />
                                        {selectErrors.tipoCombustible && (
                                            <div className="text-danger" style={{ fontSize: '0.875em', marginTop: '0.25rem' }}>{selectErrors.tipoCombustible}</div>
                                        )}
                                    </FormGroup>
                                </div>
                                <div className="col-6">
                                    <FormGroup>
                                        <Label for="traccion">Tracción *</Label>
                                        <Select
                                            id="traccion"
                                            placeholder="Seleccione..."
                                            value={formData.tipoTraccion}
                                            onChange={handleSelectTraccion}
                                            options={Array.isArray(tiposTraccion) && tiposTraccion.length > 0 ? tiposTraccion?.map(parseTiposTraccion) : []}
                                            isClearable 
                                        />
                                        {selectErrors.tipoTraccion && (
                                            <div className="text-danger" style={{ fontSize: '0.875em', marginTop: '0.25rem' }}>{selectErrors.tipoTraccion}</div>
                                        )}
                                    </FormGroup>
                                </div>
                            </div>
                        </>
                    ) : null}
                </ModalBody>
                <ModalFooter>
                    <button type="button" className="btn btn-light" onClick={handleCancel} disabled={loading}>
                        Cancelar
                    </button>
                    <button type="submit" className="btn btn-success" disabled={loading}>
                        {loading ? (
                            <>
                                <i className="ri-loader-4-line ri-spin-line me-1"></i>
                                {isCreate ? 'Guardando...' : 'Actualizando...'}
                            </>
                        ) : (
                            <>
                                <i className="ri-save-line me-1"></i>
                                {isCreate ? 'Crear' : 'Actualizar'}
                            </>
                        )}
                    </button>
                </ModalFooter>
            </Form>
            <ConfirmCloseModal
                isOpen={showConfirm}
                toggle={() => setShowConfirm(false)}
                onConfirm={handleConfirmClose}
            />
            <ModalCrearRut
                isOpen={isOwnerModalOpen && ownerModalMode === 'create'}
                toggle={() => setIsOwnerModalOpen(false)}
                onSave={handleOwnerSave}
                rut={lookupRut}
            />
            <ModalModificarCliente
                isOpen={isOwnerModalOpen && ownerModalMode === 'modify'}
                toggle={() => setIsOwnerModalOpen(false)}
                onSave={handleOwnerSave}
                selectedCliente={lookupRut}
            />
        </Modal>
    );
};

export default ModalFormVehiculo;
